// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export enum PropertyTypes {
    String,
    Numeric,
    Boolean
}

export function isContractModel(source: any): source is IContract {
    // Method Implementation: simple
    return source.modelName === 'Contract';
}

export function isPropertyModel(source: any): source is IProperty {
    // Method Implementation: simple
    return source.modelName === 'Property';
}

export function isValueModel(source: any): source is IValue {
    // Method Implementation: simple
    return source.modelName === 'Value';
}

export interface ContractZodSchemaShape {
    name?: string;

    properties?: PropertyZodSchemaShape[];
}

export interface PropertyZodSchemaShape {
    name?: string;

    type?: 'String' | 'Numeric' | 'Boolean';

    values?: ValueZodSchemaShape[];
}

export interface ValueZodSchemaShape {
    name?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const ContractZodSchema: Zod.ZodSchema<ContractZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    properties: Zod.array(PropertyZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const PropertyZodSchema: Zod.ZodSchema<PropertyZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    type: Zod.enum([ 'String', 'Numeric', 'Boolean' ]).optional(),
    values: Zod.array(ValueZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ValueZodSchema: Zod.ZodSchema<ValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional()
}).nonstrict());

export interface IContract extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly properties: Property[];

    readonly isNameDefined: boolean;

    readonly isPropertiesDefined: boolean;
}

export interface IProperty extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly type: PropertyTypes;

    readonly values: Value[];

    readonly isNameDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isValuesDefined: boolean;
}

export interface IValue extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly isNameDefined: boolean;
}

export class Contract implements IContract {
    public static readonly modelName: string = 'Contract';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'properties' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Contract.modelName;
    }

    private _name: string;

    private _properties: Property[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get properties(): Property[] {
        // Method Implementation: simple
        return this._properties;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set properties(value: Property[]) {
        // Method Implementation: simple
        this._properties = value;
        this._isPropertiesDefined = true;
    }

    private _isNameDefined: boolean;

    private _isPropertiesDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isPropertiesDefined(): boolean {
        // Method Implementation: simple
        return this._isPropertiesDefined;
    }

    public constructor();
    public constructor(source: IContract);
    public constructor(source: Partial<Pick<Contract, 'name' | 'properties'>>);
    public constructor(source?: IContract | Partial<Pick<Contract, 'name' | 'properties'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isContractModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isPropertiesDefined) {
                    this._properties = source.properties;
                    this._isPropertiesDefined = true;
                }
                else {
                    this._properties = [];
                    this._isPropertiesDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.properties !== undefined) {
                    this._properties = source.properties;
                    this._isPropertiesDefined = true;
                }
                else {
                    this._properties = [];
                    this._isPropertiesDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._properties = [];
            this._isPropertiesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Contract.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isPropertiesDefined && this.properties !== undefined)
            definedProperties.push('properties');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'properties':
                return this.properties;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isPropertiesDefined && this.properties !== undefined) {
            if (this.properties !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('properties', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.properties.length));
                for (let i = 0; i < this.properties.length; i++) {
                    const item = this.properties[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('properties', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'properties':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.properties = [];
                
                    let item: Property = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Property.modelName) {
                                item = new Property();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Property.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'properties' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'properties'`);
                        }
                
                        this.properties.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'properties' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'properties'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // properties
        if (this.isPropertiesDefined && this.properties !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.properties === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.properties.length);
            
                for (const item of this.properties) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // properties
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Property;
            
                    this.properties = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Property();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.properties.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Contract {
        // Method Implementation: simple
        const schema = ContractZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ContractZodSchemaShape): Contract {
        // Method Implementation: parseZodSchema
        return new Contract({
            name: schema.name,
            properties: schema.properties?.map(i => Property.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            properties: this.isPropertiesDefined ? (this.properties?.map(item => item)) : undefined,
        };
    }

    public clone(): Contract {
        // Method Implementation: clone
        return new Contract({
            name: this.isNameDefined ? this.name : undefined,
            properties: this.isPropertiesDefined ? this.properties?.map(i => i.clone()) : undefined
        });
    }
}

export class Property implements IProperty {
    public static readonly modelName: string = 'Property';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'type', 'values' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Property.modelName;
    }

    private _name: string;

    private _type: PropertyTypes;

    private _values: Value[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get type(): PropertyTypes {
        // Method Implementation: simple
        return this._type;
    }

    public get values(): Value[] {
        // Method Implementation: simple
        return this._values;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set type(value: PropertyTypes) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set values(value: Value[]) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    private _isNameDefined: boolean;

    private _isTypeDefined: boolean;

    private _isValuesDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public constructor();
    public constructor(source: IProperty);
    public constructor(source: Partial<Pick<Property, 'name' | 'type' | 'values'>>);
    public constructor(source?: IProperty | Partial<Pick<Property, 'name' | 'type' | 'values'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isPropertyModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = PropertyTypes.String;
                    this._isTypeDefined = false;
                }
                
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = PropertyTypes.String;
                    this._isTypeDefined = false;
                }
                
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._type = PropertyTypes.String;
            this._isTypeDefined = false;
        
            this._values = [];
            this._isValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Property.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'type':
                return this.type;
            case 'values':
                return this.values;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.type = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'type' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: Value = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Value.modelName) {
                                item = new Value();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Value.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'values' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.type);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.type = reader.readEnumValue();
        }
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Value;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Value();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Property {
        // Method Implementation: simple
        const schema = PropertyZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: PropertyZodSchemaShape): Property {
        // Method Implementation: parseZodSchema
        return new Property({
            name: schema.name,
            type: (() => {
            if (!schema.type)
                return;
        
            switch (schema.type) {
                case 'String':
                    return PropertyTypes.String;
                case 'Numeric':
                    return PropertyTypes.Numeric;
                case 'Boolean':
                    return PropertyTypes.Boolean;
                default:
                    throw new Error(`Enum type "schema.type" not recognized`);
            }
        })(),
            values: schema.values?.map(i => Value.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            type: this.isTypeDefined ? (PropertyTypes[this.type]) : undefined,
            values: this.isValuesDefined ? (this.values?.map(item => item)) : undefined,
        };
    }

    public clone(): Property {
        // Method Implementation: clone
        return new Property({
            name: this.isNameDefined ? this.name : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            values: this.isValuesDefined ? this.values?.map(i => i.clone()) : undefined
        });
    }
}

export class Value implements IValue {
    public static readonly modelName: string = 'Value';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Value.modelName;
    }

    private _name: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    private _isNameDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public constructor();
    public constructor(source: IValue);
    public constructor(source: Partial<Pick<Value, 'name'>>);
    public constructor(source?: IValue | Partial<Pick<Value, 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isValueModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Value.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Value {
        // Method Implementation: simple
        const schema = ValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ValueZodSchemaShape): Value {
        // Method Implementation: parseZodSchema
        return new Value({
            name: schema.name
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
        };
    }

    public clone(): Value {
        // Method Implementation: clone
        return new Value({
            name: this.isNameDefined ? this.name : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Contract':
                return new Contract();
            case 'Property':
                return new Property();
            case 'Value':
                return new Value();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Contract':
                return new Contract(source);
            case 'Property':
                return new Property(source);
            case 'Value':
                return new Value(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Contract':
                return Contract.parse(source);
            case 'Property':
                return Property.parse(source);
            case 'Value':
                return Value.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
